import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, ArrowRight, Zap, Music, Video, Cpu, Activity, Mic, Type, Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react';
import { demofile } from '../utils/paths';
import LazyVideo from './LazyVideo';
import { useLanguage } from '../contexts/LanguageContext';
import { getT } from '../utils/translations';

interface SoundGenerationIntroProps {
  onBack: () => void;
  onNavigateToExperience?: (tabId: string) => void;
}

// ================================
// Ability Intro Materials (replace old)
// Source: repo root `./音效生成`（含 TC-V2A、AC-V2A、视频音效生成V2A、文生音效 TTA）
// 同步至 `public/demofile/mp4|mp3/音效生成/`.
// ================================

const demofileMp4 = (...parts: string[]): string =>
  demofile(`mp4/${parts.map(encodeURIComponent).join('/')}`);

const demofileMp3 = (...parts: string[]): string =>
  demofile(`mp3/${parts.map(encodeURIComponent).join('/')}`);

type TCExample = {
  key: string;
  inputSrc: string;
  promptSrc: string;
  outputSrc: string;
};

const TC_V2A_GROUPS: { id: string; labelZh: string; labelEn: string; items: TCExample[] }[] = [
  {
    id: 'tc-l0',
    labelZh: 'L0',
    labelEn: 'L0',
    items: [1, 2].map((n) => ({
      key: `TC-L0-${n}`,
      inputSrc: demofileMp4('音效生成', 'TC-V2A', 'L0', '示例' + n, 'input.mp4'),
      promptSrc: demofileMp4('音效生成', 'TC-V2A', 'L0', '示例' + n, '提示词.txt'),
      outputSrc: demofileMp4('音效生成', 'TC-V2A', 'L0', '示例' + n, 'output.mp4'),
    })),
  },
  {
    id: 'tc-l1-action',
    labelZh: 'L1_action',
    labelEn: 'L1_action',
    items: [1, 2].map((n) => ({
      key: `TC-L1-action-${n}`,
      inputSrc: demofileMp4('音效生成', 'TC-V2A', 'L1', 'L1_action', '示例' + n, 'input.mp4'),
      promptSrc: demofileMp4('音效生成', 'TC-V2A', 'L1', 'L1_action', '示例' + n, '提示词.txt'),
      outputSrc: demofileMp4('音效生成', 'TC-V2A', 'L1', 'L1_action', '示例' + n, 'output.mp4'),
    })),
  },
  {                                                                                                                                                                      
    id: 'tc-l1-subject',                                                                                                                                                 
    labelZh: 'L1_subject',                                                                                                                                               
    labelEn: 'L1_subject',                                                                                                                                               
    items: [1, 2].map((n) => ({                                                                                                                                    
      key: `TC-L1-subject-${n}`,                                                                                                                                  
      inputSrc: demofileMp4('音效生成', 'TC-V2A', 'L1', 'L1_subject', '示例' + n, 'input.mp4'),                                                                   
      promptSrc: demofileMp4('音效生成', 'TC-V2A', 'L1', 'L1_subject', '示例' + n, '提示词.txt'),                                                                 
      outputSrc: demofileMp4('音效生成', 'TC-V2A', 'L1', 'L1_subject', '示例' + n, 'output.mp4'),                                                                 
      })),                                                                                                                                                          
  },                                                                                                                                                              
  {                                                                                                                                                               
    id: 'tc-l2',                                                                                                                                                  
    labelZh: 'L2',                                                                                                                                                
    labelEn: 'L2',                                                                                                                                                
    items: [1, 2].map((n) => ({                                                                                                                             
     key: `TC-L2-${n}`,                                                                                                                                          
     inputSrc: demofileMp4('音效生成', 'TC-V2A', 'L2', '示例' + n, 'input.mp4'),                                                                                 
     promptSrc: demofileMp4('音效生成', 'TC-V2A', 'L2', '示例' + n, '提示词.txt'),                                                                               
     outputSrc: demofileMp4('音效生成', 'TC-V2A', 'L2', '示例' + n, 'output.mp4'),                                                                               
    })),                                                                                                                                                          
  },  
];

type ACExample = {
  key: string;
  refAudioSrc: string;
  refAudioName: string;
  outputVideoSrc: string;
};

const AC_V2A_EXAMPLES: ACExample[] = [
  {
    key: 'AC-1',
    refAudioSrc: demofileMp3('音效生成', 'AC-V2A', '示例1', 'Train horn.mp3'),
    refAudioName: 'Train horn.mp3',
    outputVideoSrc: demofileMp4('音效生成', 'AC-V2A', '示例1', 'output_002.mp4'),
  },
  {
    key: 'AC-2',
    refAudioSrc: demofileMp3('音效生成', 'AC-V2A', '示例2', 'erhu1.mp3'),
    refAudioName: 'erhu1.mp3',
    outputVideoSrc: demofileMp4('音效生成', 'AC-V2A', '示例2', 'output_004.mp4'),
  },
  {
    key: 'AC-3',
    refAudioSrc: demofileMp3('音效生成', 'AC-V2A', '示例3', 'war_drum.mp3'),
    refAudioName: 'war_drum.mp3',
    outputVideoSrc: demofileMp4('音效生成', 'AC-V2A', '示例3', 'output_001 (1).mp4'),
  },
];

/** 视频音效生成 V2A：按场景分类的示例视频（仅展示生成结果） */
const V2A_GROUPS: { id: string; folderZh: string; labelZh: string; labelEn: string; files: string[] }[] = [
  {
    id: 'v2a-nature',
    folderZh: '自然',
    labelZh: '自然',
    labelEn: 'Nature',
    files: [
      '10247.mp4',
      '10261.mp4',
      '31e18709-ca96-4f9f-ba68-e05f77f3398a_3.mp4',
      '483a020b-b1c9-434a-bcdf-06eb28276ac3_3.mp4',
      'e5c0e83b-413b-4a5c-8f71-b55a170f3c3d_4.mp4',
      '-vNGqcDA6oE_000020.mp4',
    ],
  },
  {
    id: 'v2a-instrument',
    folderZh: '乐器',
    labelZh: '乐器',
    labelEn: 'Instruments',
    files: [
      '1_0T2ZIjWnw_000031.mp4',
      // '284d400b-1d5c-4736-87b7-2718c8d59ad3_3.mp4',
      '307eb6fc-b1cd-4a58-901e-8c3506fbb57c_2.mp4',
      'fd0fd626-104f-45a7-9be2-67f6d5a63256_4.mp4',
      '-rTTPoBJNI4_000123.mp4',
    ],
  },
  {
    id: 'v2a-daily',
    folderZh: '日常',
    labelZh: '日常',
    labelEn: 'Daily life',
    files: [
      '0VW4nelMODc_000271.mp4',
      '10199.mp4',
      '10312.mp4',
      '10745.mp4',
      '24tWz7gmngI_000030.mp4',
      '2_C5yMbxKpY_000011.mp4',
      'e2f8b819-87f2-4bf4-b1ab-c932d412da61_3.mp4',
    ],
  },
  {
    id: 'v2a-game',
    folderZh: '游戏',
    labelZh: '游戏',
    labelEn: 'Game',
    files: ['346cf975-2dad-46a1-9d72-5c3d45a9e9bc_2.mp4'],
  },
  {
    id: 'v2a-people',
    folderZh: '人物',
    labelZh: '人物',
    labelEn: 'People',
    files: ['output_001.mp4', '-wwPo59cjuo_000334.mp4'],
  },
  {
    id: 'v2a-machine',
    folderZh: '机械',
    labelZh: '机械',
    labelEn: 'Machinery',
    files: ['10014.mp4', '10165.mp4', '16139.mp4', 'output_002.mp4'],
  },
  {
    id: 'v2a-animal',
    folderZh: '动物',
    labelZh: '动物',
    labelEn: 'Animals',
    files: ['0F04c_rY4aw_000000.mp4', '10328.mp4'],
  },
];

type TtaItem = {
  key: string;
  labelZh: string;
  labelEn: string;
  audioFile: string;
  pathParts: string[];
};

const TTA_GROUPS: { id: string; labelZh: string; labelEn: string; items: TtaItem[] }[] = [
  {
    id: 'tta-animal',
    labelZh: '动物音效',
    labelEn: 'Animal sounds',
    items: [
      { key: 'bird', labelZh: '鸟叫声', labelEn: 'Birdsong', audioFile: 'A_bird_sings_melodically_in_a__inference_2 (2).flac', pathParts: ['动物音效', '鸟叫声'] },
      { key: 'sheep', labelZh: '羊叫声', labelEn: 'Sheep', audioFile: '羊叫.flac', pathParts: ['动物音效', '羊叫声'] },
      { key: 'cat', labelZh: '猫叫声', labelEn: 'Cat', audioFile: '猫叫.flac', pathParts: ['动物音效', '猫叫声'] },
      { key: 'dog', labelZh: '狗叫声', labelEn: 'Dog', audioFile: '狗叫.flac', pathParts: ['动物音效', '狗叫声'] },
    ],
  },
  {
    id: 'tta-human',
    labelZh: '人类音效',
    labelEn: 'Human sounds',
    items: [
      { key: 'snore', labelZh: '打呼噜', labelEn: 'Snoring', audioFile: '呼噜声.flac', pathParts: ['人类音效', '打呼噜'] },
      { key: 'typing', labelZh: '打字声', labelEn: 'Typing', audioFile: '键盘打字声.flac', pathParts: ['人类音效', '打字声'] },
      { key: 'steps', labelZh: '脚步声', labelEn: 'Footsteps', audioFile: '脚步声.flac', pathParts: ['人类音效', '脚步声'] },
    ],
  },
  {
    id: 'tta-instrument',
    labelZh: '乐器音效',
    labelEn: 'Instruments',
    items: [
      { key: 'guitar', labelZh: '吉他', labelEn: 'Guitar', audioFile: '吉他.flac', pathParts: ['乐器音效', '吉他'] },
      { key: 'drums', labelZh: '架子鼓', labelEn: 'Drums', audioFile: '架子鼓.flac', pathParts: ['乐器音效', '架子鼓'] },
      { key: 'piano', labelZh: '钢琴', labelEn: 'Piano', audioFile: '弹钢琴.flac', pathParts: ['乐器音效', '钢琴'] },
    ],
  },
  {
    id: 'tta-nature',
    labelZh: '自然音效',
    labelEn: 'Nature',
    items: [
      { key: 'stream', labelZh: '小河流水', labelEn: 'Stream', audioFile: '小河流水.flac', pathParts: ['自然音效', '小河流水'] },
      { key: 'rain', labelZh: '雨滴声', labelEn: 'Rain', audioFile: '雨滴声.flac', pathParts: ['自然音效', '雨滴声'] },
      // { key: 'wave', labelZh: '海浪声', labelEn: 'Waves', audioFile: '海浪声.flac', pathParts: ['自然音效', '海浪声'] },
      { key: 'thunder', labelZh: '雷声', labelEn: 'Thunder', audioFile: '雷声.flac', pathParts: ['自然音效', '雷声'] },
      // { key: 'insect', labelZh: '森林虫鸣', labelEn: 'Insects', audioFile: '虫鸣.flac', pathParts: ['自然音效', '森林虫鸣'] },
      { key: 'campfire', labelZh: '篝火声', labelEn: 'Campfire', audioFile: '篝火.flac', pathParts: ['自然音效', '篝火声'] },
    ],
  },
  {
    id: 'tta-other',
    labelZh: '其他音效',
    labelEn: 'Other',
    items: [
      { key: 'alarm', labelZh: '电子闹钟', labelEn: 'Alarm clock', audioFile: '闹钟声.flac', pathParts: ['其他音效', '电子闹钟'] },
      { key: 'vehicle', labelZh: '车辆', labelEn: 'Vehicle', audioFile: 'Motorcycle slows, and then speeds up._3.flac', pathParts: ['其他音效', '车辆'] },
    ],
  },
];

const TTA_SHORT: Record<string, Record<string, { zh: string; en: string }>> = {
  'tta-instrument': {
    guitar: { zh: '清脆指弹吉他，温暖木质音色', en: 'Fingerstyle acoustic guitar, warm tone' },
    drums:  { zh: '动感流行鼓组，清晰有力节奏', en: 'Punchy pop drum kit, tight groove' },
    piano:  { zh: '明快钢琴旋律，清澈音色',   en: 'Bright piano melody, clear tone' },
  },
  'tta-nature': {
    stream:  { zh: '小河潺潺，柔和水流',       en: 'Gentle babbling stream' },
    rain:    { zh: '窗外细雨，均匀落点',       en: 'Light rain on window, even taps' },
    // wave:    { zh: '海浪起落，舒缓节奏',       en: 'Ocean waves, rolling rhythm' },
    thunder: { zh: '低沉雷鸣，空间感强',       en: 'Deep thunderclap with spacious tail' },
    // insect:  { zh: '夏夜虫鸣，细密清晰',       en: 'Summer night insects, fine and clear' },
    campfire:{ zh: '篝火劈啪作响，温暖氛围',   en: 'Crackling campfire, warm ambience' },
  },
  'tta-animal': {
    bird: { zh: '清脆鸟鸣，林间回声', en: 'Crisp birdsong with forest ambience' },
    sheep:{ zh: '单只绵羊咩叫',       en: 'Single sheep bleat' },
    cat:  { zh: '单只猫叫',           en: 'Single cat meow' },
    dog:  { zh: '清晰犬吠',           en: 'Clear dog bark' },
  },
  'tta-human': {
    snore:  { zh: '低沉均匀鼾声',         en: 'Deep, even snoring' },
    typing: { zh: '机械键盘打字，清脆节奏', en: 'Mechanical keyboard typing, crisp rhythm' },
    steps:  { zh: '木地板脚步声，平稳节奏', en: 'Footsteps on wooden floor, steady pace' },
  },
  'tta-other': {
    alarm:   { zh: '电子闹钟提示音',       en: 'Electronic alarm beep' },
    vehicle: { zh: '摩托起步与加速',       en: 'Motorcycle start then accelerate' },
  },
};

function ttaShortPrompt(groupId: string, itemKey: string, lang: 'zh' | 'en') {
  const g = TTA_SHORT[groupId];
  const rec = g?.[itemKey];
  if (!rec) return lang === 'zh' ? '示例音频' : 'Sample audio';
  return lang === 'zh' ? rec.zh : rec.en;
}

function ttaPromptSrc(item: TtaItem): string {
  return demofileMp3('音效生成', '文生音效 TTA', ...item.pathParts, '提示词.txt');
}

function ttaAudioSrc(item: TtaItem): string {
  return demofileMp3('音效生成', '文生音效 TTA', ...item.pathParts, item.audioFile);
}

function ttaPromptKey(groupId: string, itemKey: string): string {
  return `tta-${groupId}-${itemKey}`;
}

const MAIN_TAB_META: { id: 'ac' | 'tc' | 'v2a' | 'tta'; labelZh: string; labelEn: string }[] = [
  { id: 'tta', labelZh: '文生音效', labelEn: 'Text-to-Audio' },
  { id: 'v2a', labelZh: '视频生音效', labelEn: 'Video-to-Audio' },
  { id: 'tc',  labelZh: '文本控制视频生音效', labelEn: 'Text-Controlled Video-to-Audio' },
  { id: 'ac',  labelZh: '音频控制视频生音效', labelEn: 'Audio-Controlled Video-to-Audio' },
];

export default function SoundGenerationIntro({ onBack, onNavigateToExperience }: SoundGenerationIntroProps) {
  const { lang } = useLanguage();
  const t = getT(lang);
  const gi = t.genIntro;

  const [abilityTab, setAbilityTab] = useState<'ac' | 'tc' | 'v2a' | 'tta'>('tta');
  const [tcGroupId, setTcGroupId] = useState(TC_V2A_GROUPS[0]?.id ?? 'tc-l0');
  const [v2aGroupId, setV2aGroupId] = useState(V2A_GROUPS[0]?.id ?? 'v2a-nature');
  const [ttaGroupId, setTtaGroupId] = useState(TTA_GROUPS[0]?.id ?? 'tta-animal');
  const [showMainContent, setShowMainContent] = useState(false);
  const [showBelowFold, setShowBelowFold] = useState(false);

  /** TC-V2A / TTA：从 提示词.txt 加载，与示例一一对应 */
  const [promptMap, setPromptMap] = useState<Record<string, string>>({});
  const promptsLoadedRef = useRef(false);

  /** 视频 V2A：主画面 + 侧栏缩略（对齐 audiodemo） */
  const [v2aFileIdx, setV2aFileIdx] = useState(0);
  /** TTA：全局单路播放（对齐 audiodemo 文生音效区） */
  const ttaAudioRef = useRef<HTMLAudioElement | null>(null);
  const [ttaPlayingId, setTtaPlayingId] = useState<string | null>(null);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);
  useEffect(() => { setV2aFileIdx(0); }, [v2aGroupId]);

  useEffect(() => {
    return () => {
      if (ttaAudioRef.current) {
        ttaAudioRef.current.pause();
        ttaAudioRef.current.src = '';
        ttaAudioRef.current = null;
      }
    };
  }, []);

  const pauseTta = () => {
    ttaAudioRef.current?.pause();
    setTtaPlayingId(null);
  };

  const toggleTtaPlay = (playKey: string, url: string) => {
    if (ttaPlayingId === playKey) {
      ttaAudioRef.current?.pause();
      setTtaPlayingId(null);
      return;
    }
    let audio = ttaAudioRef.current;
    if (!audio) {
      audio = new Audio();
      audio.addEventListener('ended', () => setTtaPlayingId(null));
      ttaAudioRef.current = audio;
    }
    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    audio
      .play()
      .then(() => setTtaPlayingId(playKey))
      .catch(() => setTtaPlayingId(null));
  };

  useEffect(() => {
    const t1 = window.setTimeout(() => setShowMainContent(true), 50);
    return () => clearTimeout(t1);
  }, []);
  useEffect(() => {
    const t2 = window.setTimeout(() => setShowBelowFold(true), 250);
    return () => clearTimeout(t2);
  }, []);

  useEffect(() => {
    if (!showMainContent || !showBelowFold) return;
    if (promptsLoadedRef.current) return;
    promptsLoadedRef.current = true;

    const load = async () => {
      const tcLoads = TC_V2A_GROUPS.flatMap((g) => g.items).map((item) => ({
        key: item.key,
        src: item.promptSrc,
      }));
      const ttaLoads = TTA_GROUPS.flatMap((g) =>
        g.items.map((it) => ({
          key: ttaPromptKey(g.id, it.key),
          src: ttaPromptSrc(it),
        })),
      );
      await Promise.all(
        [...tcLoads, ...ttaLoads].map(async ({ key, src }) => {
          try {
            const res = await fetch(src);
            const txt = (await res.text()).trim();
            setPromptMap((prev) => ({ ...prev, [key]: txt }));
          } catch {
            setPromptMap((prev) => ({ ...prev, [key]: '' }));
          }
        }),
      );
    };
    load();
  }, [showMainContent, showBelowFold]);

  const showcaseTitle = lang === 'zh' ? '音效展示' : 'Sound Showcase';
  const showcaseSub = lang === 'zh'
    ? '选择能力类型与示例子类，查看对应素材'
    : 'Choose a capability and subcategory to view demos';

  const activeTcGroup = TC_V2A_GROUPS.find((g) => g.id === tcGroupId) ?? TC_V2A_GROUPS[0];
  const activeV2aGroup = V2A_GROUPS.find((g) => g.id === v2aGroupId) ?? V2A_GROUPS[0];
  const activeTtaGroup = TTA_GROUPS.find((g) => g.id === ttaGroupId) ?? TTA_GROUPS[0];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 animate-in fade-in duration-300 relative">
      {/* Back button — always visible for immediate feedback when entering from 能力介绍 */}
      <button
        onClick={onBack}
        className="fixed top-4 sm:top-6 left-4 sm:left-6 z-50 flex items-center text-gray-600 hover:text-primary transition-colors group"
      >
        <div className="p-1.5 rounded-full bg-gray-100 group-hover:bg-blue-50 transition-colors mr-2">
          <ArrowLeft className="w-4 h-4" />
        </div>
        <span className="font-medium text-sm sm:text-base">{t.common.backHome}</span>
      </button>

      {/* First frame only: title so page is not blank; avoids full-page spinner feel */}
      {!showMainContent && (
        <section className="pt-24 sm:pt-28 px-4 sm:px-6 max-w-[1500px] mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{gi.title}</h1>
        </section>
      )}

      {showMainContent && (
      <>
      {/* Hero */}
      <section className="pt-20 sm:pt-24 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-12 max-w-[1500px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-12">
          <div className="w-full lg:w-1/2">
            <div className="mb-4 sm:mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold">
                <Zap size={14} />
                <span>{gi.badge}</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 leading-tight">
              {gi.title}
            </h1>
            <p className="text-base sm:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8">{gi.desc}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="flex items-center gap-2 text-gray-700 font-medium bg-gray-50 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
                <Mic className="text-purple-500" size={18} />{gi.tag1}
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-medium bg-gray-50 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
                <Video className="text-blue-500" size={18} />{gi.tag2}
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-medium bg-gray-50 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
                <Type className="text-orange-500" size={18} />{gi.tag3}
              </div>
              <div className="flex items-center gap-2 text-gray-700 font-medium bg-gray-50 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
                <Music className="text-pink-500" size={18} />{gi.tag4}
              </div>
            </div>
            {onNavigateToExperience && (
              null
            )}
          </div>
          <div className="w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-xs sm:max-w-sm lg:max-w-lg aspect-square bg-gradient-to-br from-blue-100 to-indigo-50 rounded-full flex items-center justify-center p-6 sm:p-8">
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 rounded-full"></div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full h-full relative z-10">
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center justify-center transform translate-y-4">
                  <Video className="w-8 h-8 sm:w-12 sm:h-12 text-blue-500 mb-2 sm:mb-4" />
                  <span className="font-bold text-gray-800 text-sm sm:text-base">{lang === 'zh' ? '视觉理解' : 'Visual Understanding'}</span>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center justify-center transform -translate-y-4">
                  <Activity className="w-8 h-8 sm:w-12 sm:h-12 text-indigo-500 mb-2 sm:mb-4" />
                  <span className="font-bold text-gray-800 text-sm sm:text-base">{lang === 'zh' ? '物理模拟' : 'Physics Simulation'}</span>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center justify-center transform translate-y-4">
                  <Mic className="w-8 h-8 sm:w-12 sm:h-12 text-purple-500 mb-2 sm:mb-4" />
                  <span className="font-bold text-gray-800 text-sm sm:text-base">{lang === 'zh' ? '文本控制' : 'Text Control'}</span>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center justify-center transform -translate-y-4">
                  <Music className="w-8 h-8 sm:w-12 sm:h-12 text-pink-500 mb-2 sm:mb-4" />
                  <span className="font-bold text-gray-800 text-sm sm:text-base">{lang === 'zh' ? '高保真音频' : 'Hi-Fi Audio'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-6 sm:py-8 bg-white px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">{gi.techTitle}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {[
              { icon: <Cpu size={24} />, color: 'blue', title: gi.feature1Title, desc: gi.feature1Desc },
              { icon: <Music size={24} />, color: 'purple', title: gi.feature2Title, desc: gi.feature2Desc },
              { icon: <Activity size={24} />, color: 'indigo', title: gi.feature3Title, desc: gi.feature3Desc },
            ].map((f, i) => (
              <div key={i} className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-${f.color}-100 rounded-xl flex items-center justify-center text-${f.color}-600 mb-4 sm:mb-6`}>
                  {f.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{f.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </>
      )}

      {/* 音效展示：版式对齐 demo-web-front_V2/audiodemo */}
      {showMainContent && showBelowFold && (
      <section className="py-12 bg-white px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-center text-blue-600">{showcaseTitle}</h2>
          <p className="text-center text-gray-500 text-sm sm:text-base mb-8">{showcaseSub}</p>

          {/* 一级 Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {MAIN_TAB_META.map((tab) => {
              const isActive = abilityTab === tab.id;
              const label = lang === 'zh' ? tab.labelZh : tab.labelEn;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    pauseTta();
                    setAbilityTab(tab.id);
                  }}
                  className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-bold transition-all text-xs sm:text-sm shadow-sm ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-blue-200 scale-105'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* TTA：与 audiodemo 相同 — 灰底圆角容器 + 渐变二级标签 + 卡片播放 */}
          {abilityTab === 'tta' && activeTtaGroup && (
            <div className="bg-gray-50 rounded-3xl p-6 lg:p-8 border border-gray-100 shadow-sm animate-in fade-in duration-500 min-h-[320px]">
              <div className="mb-4">
                <div className="inline-flex items-center gap-2">
                  <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                  <p className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与文本描述相匹配的音频' : 'Generate audio from text descriptions'}</p>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {TTA_GROUPS.map((group) => {
                  const isActive = ttaGroupId === group.id;
                  const title = lang === 'zh' ? group.labelZh : group.labelEn;
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => {
                        pauseTta();
                        setTtaGroupId(group.id);
                      }}
                      className={`px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-all text-sm ${
                        isActive
                          ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg'
                          : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {title}
                    </button>
                  );
                })}
              </div>

              <div className="mb-6">
                <div className="flex justify-center items-center gap-3">
                  {/* <div className="w-1.5 h-5 bg-blue-500 rounded-full shrink-0" /> */}
                  <h3 className="font-semibold text-lg sm:text-xl text-gray-800">
                    {lang === 'zh' ? activeTtaGroup.labelZh : activeTtaGroup.labelEn}
                  </h3>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {activeTtaGroup.items.map((it) => {
                  const pk = ttaPromptKey(activeTtaGroup.id, it.key);
                  const playKey = `${activeTtaGroup.id}-${it.key}`;
                  const isPlaying = ttaPlayingId === playKey;
                  const name = lang === 'zh' ? it.labelZh : it.labelEn;
                  const promptText = ttaShortPrompt(activeTtaGroup.id, it.key, lang);
                  return (
                    <div
                      key={it.key}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-4 flex flex-col gap-3 w-full sm:w-[calc(50%-0.5rem)] lg:w-[280px] max-w-full"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl shrink-0" aria-hidden>🎵</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-800 font-bold text-sm">{name}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-4 leading-relaxed">{promptText}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleTtaPlay(playKey, ttaAudioSrc(it))}
                          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
                            isPlaying
                              ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
                              : 'bg-gradient-to-r from-purple-400 to-purple-500 text-white hover:from-purple-500 hover:to-purple-600'
                          }`}
                          aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                          {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AC / TC / V2A：audiodemo 灰底容器 + 蓝系二级 Tab */}
          {abilityTab !== 'tta' && (
            <div className="bg-gray-50 rounded-3xl p-6 lg:p-8 border border-gray-100 shadow-sm animate-in fade-in duration-500">
              {abilityTab === 'tc' && (
                <>
                  <div className="mb-4">
                    {lang === 'zh' ? (
                      <div className="text-gray-600 text-sm sm:text-base leading-snug">
                        <div className="inline-flex items-center gap-2">
                          <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                          <div className="font-semibold text-lg sm:text-xl text-gray-800">根据文本提示生成对齐的音频，同时保持与视频同步</div>
                        </div>
                        <div className="mt-1">L0-L2 表示文本-视频冲突逐步提升（L0 表示两者一致，L1 表示动作或对象不一致，L2 表示动作与对象均不一致）</div>
                      </div>
                    ) : (
                      <div className="text-gray-600 text-sm sm:text-base leading-snug">
                        <div className="inline-flex items-center gap-2">
                          <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                          <div className="font-semibold text-lg sm:text-xl text-gray-800">Generate audio aligned with text prompts while staying synchronized with the video</div>
                        </div>
                        <div className="mt-1">L0-L2 indicate increasing text-video conflict (L0 aligned; L1 action or subject mismatch; L2 both action and subject mismatch)</div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 mb-8 border-b border-gray-200 pb-4">
                    {TC_V2A_GROUPS.map((group) => {
                      const isActive = tcGroupId === group.id;
                      const title = lang === 'zh' ? group.labelZh : group.labelEn;
                      return (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => setTcGroupId(group.id)}
                          className={`px-4 py-1.5 rounded-md font-medium transition-all text-sm ${
                            isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          {title}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {abilityTab === 'v2a' && (
                <>
                  <div className="mb-4">
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                      <p className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与视频内容相匹配的音频' : 'Generate audio that matches video content'}</p>
                    </div>
                  </div>
                <div className="flex flex-wrap justify-center gap-2 mb-8 border-b border-gray-200 pb-4">
                  {V2A_GROUPS.map((group) => {
                    const isActive = v2aGroupId === group.id;
                    const title = lang === 'zh' ? group.labelZh : group.labelEn;
                    return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => setV2aGroupId(group.id)}
                        className={`px-4 py-1.5 rounded-md font-medium transition-all text-sm ${
                          isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                        }`}
                      >
                        {title}
                      </button>
                    );
                  })}
                </div>
                </> 
              )}

              {abilityTab === 'ac' && (
                <>
                  <div className="mb-4">
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                      <p className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与参考音频音色一致、视频时间同步的音频' : 'Generate audio with timbre matching reference audio while staying synchronized with the video'}</p>
                    </div>
                  </div>
                  {/* <div className="mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-5 bg-blue-500 rounded-full shrink-0" />
                      <h3 className="font-semibold text-lg sm:text-xl text-gray-800">
                        {lang === 'zh' ? '参考音频与输出视频' : 'Reference audio and output video'}
                      </h3>
                    </div>
                  </div> */}
                </>
              )}

              {abilityTab === 'tc' && activeTcGroup && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg sm:text-xl text-gray-800 text-center">
                    {lang === 'zh' ? activeTcGroup.labelZh : activeTcGroup.labelEn}
                  </h3>
                </div>
              )}

              {abilityTab === 'v2a' && activeV2aGroup && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg sm:text-xl text-gray-800 text-center">
                    {lang === 'zh' ? activeV2aGroup.labelZh : activeV2aGroup.labelEn}
                  </h3>
                </div>
              )}

              {/* AC-V2A：参考音频与输出视频同卡展示，无箭头 */}
              {abilityTab === 'ac' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {AC_V2A_EXAMPLES.map((ex) => (
                    <div key={ex.key} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-3 sm:p-4 w-full sm:max-w-[760px] md:max-w-[820px] lg:max-w-[860px] mx-auto">
                      <div className="space-y-3">
                        <div className="rounded-xl border border-gray-200 bg-white p-2 sm:p-3">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                            {lang === 'zh' ? '输入（参考音频）' : 'Input (reference audio)'}
                          </p>
                          <div className="rounded-xl overflow-hidden bg-white">
                            <audio src={ex.refAudioSrc} controls className="w-full h-10 block" preload="metadata" />
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white p-2 sm:p-3">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                            {lang === 'zh' ? '输出' : 'Output'}
                          </p>
                          <div className="rounded-2xl overflow-hidden bg-gray-900 aspect-video shadow w-full sm:w-[300px] md:w-[340px] mx-auto">
                            <LazyVideo
                              src={ex.outputVideoSrc}
                              className="w-full h-full object-cover"
                              controls
                              muted
                              playsInline
                              preload="metadata"
                              onPlay={(e) => { e.currentTarget.muted = false; }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* TC-V2A：输入 → 输出 + 提示词 */}
              {abilityTab === 'tc' && activeTcGroup && (
                <div className="flex flex-col gap-8">
                  {activeTcGroup.items.map((ex) => (
                    <div key={ex.key} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-3 sm:p-4 w-full sm:max-w-[760px] md:max-w-[820px] lg:max-w-[860px] mx-auto">
                      {promptMap[ex.key] ? (
                        <div className="mb-3">
                          <p className="text-sm sm:text-base text-gray-600">
                            <span className="font-medium text-gray-600">{lang === 'zh' ? '提示词：' : 'Prompt: '}</span>
                            {promptMap[ex.key]}
                          </p>
                        </div>
                      ) : null}
                      <div className="flex flex-col lg:flex-row items-center justify-center gap-3 sm:gap-4">
                        <div className="w-full lg:flex-1">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                            {lang === 'zh' ? '输入' : 'Input'}
                          </p>
                          <div className="rounded-2xl overflow-hidden bg-gray-900 aspect-video shadow w-full sm:w-[300px] md:w-[340px] mx-auto">
                            <LazyVideo
                              src={ex.inputSrc}
                              className="w-full h-full object-cover"
                              controls
                              muted
                              playsInline
                              preload="metadata"
                              onPlay={(e) => { e.currentTarget.muted = false; }}
                            />
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center justify-center rotate-90 lg:rotate-0 mx-2">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-sm border bg-blue-50 border-blue-200">
                            <ArrowRight size={16} className="text-blue-600" />
                          </div>
                        </div>
                        <div className="w-full lg:flex-1">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                            {lang === 'zh' ? '输出' : 'Output'}
                          </p>
                          <div className="rounded-2xl overflow-hidden bg-gray-900 aspect-video shadow w-full sm:w-[300px] md:w-[340px] mx-auto">
                            <LazyVideo
                              src={ex.outputSrc}
                              className="w-full h-full object-cover"
                              controls
                              muted
                              playsInline
                              preload="metadata"
                              onPlay={(e) => { e.currentTarget.muted = false; }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 视频 V2A：单行居中展示（最多3个） */}
              {abilityTab === 'v2a' && activeV2aGroup && (
                <div className="flex flex-wrap justify-center gap-4">
                  {activeV2aGroup.files.slice(0,3).map((fn) => {
                    const src = demofileMp4('音效生成', '视频音效生成V2A', activeV2aGroup.folderZh, fn);
                    return (
                      <div key={fn} className="w-full sm:w-[320px] md:w-[360px]">
                        <div className="rounded-2xl overflow-hidden shadow-lg bg-black aspect-video border border-gray-200">
                          <LazyVideo
                            src={src}
                            className="w-full h-full object-contain"
                            controls
                            muted
                            playsInline
                            preload="metadata"
                            onPlay={(e) => { e.currentTarget.muted = false; }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      )}
    </div>
  );
}
